import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetCountryListForDropdowns } from "../../../../../../../../api/services/client/clientApis";
import { useGetProgramClosureData } from "../../../../../../../../api/services/program/programApis";
import {
  AccordionHeaderText,
  ITabs,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  MuiTypography,
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../../../../components";
import { PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_SITECORE_KEY } from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getValueOrBlank,
  setApiDataToDropDownOption
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ProgramClosureDetailsWrapper } from "./ViewProgramClosureDetails.style";
import { EmployeeInformation } from "./tabs/employeeInformation/EmployeeInformation";
import { GeneralInformation } from "./tabs/generalInformation/GeneralInformation";

interface ViewProgramClosureDetailsProps {
  pageId: string;
  open: boolean;
  setShowModal: (value: boolean) => void;
  programId: string;
  statusId: string;
  deliveryCountry?: string;
}

export const ViewProgramClosureDetails: FC<ViewProgramClosureDetailsProps> = ({
  pageId,
  open,
  setShowModal,
  programId,
  statusId,
  deliveryCountry = "",
}) => {
  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const [selectedTab, setSelectedTab] = useState(0);
  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const {
    data: programClosureData,
    isPending: isProgramClosureDataPending,
    isError: isProgramClosureDataError,
  } = useGetProgramClosureData(programId, statusId);

  const {
    data: countryListData,
    isFetching: countryPending,
    isError: countryError,
  } = useGetCountryListForDropdowns();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isProgramClosureDataPending || countryPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isProgramClosureDataPending, countryPending]);

  useEffect(() => {
    if (isProgramClosureDataError || countryError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProgramClosureDataError, countryError]);

  const renderHeader = (key: string) => {
    return (
      <MuiBox className="closure-header">
        <AccordionHeaderText label={getLabel(key)} />
      </MuiBox>
    );
  };

  const tabIdGeneral = `${pageId}-tab1`;
  const tabIdEmployer = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: getLabel(programSiteCoreKeys.general),
    },
    {
      id: tabIdEmployer,
      label: getLabel(programSiteCoreKeys.employerInformationTab),
    },
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  return (
    <MuiModal
      onClose={() => setShowModal(false)}
      title={getLabel(programSiteCoreKeys.viewStatusDetailsHeader)}
      open={open}
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      hasLargeContent
      onSecondaryButtonClick={() => setShowModal(false)}
    >
      <ProgramClosureDetailsWrapper>
        <MuiBox className="status-container">
          <MuiTypography variant="h3" className="status-label">
            {getLabel(programSiteCoreKeys.status)}
          </MuiTypography>
          <StatusBadge
            status={STATUS_BADGE_TYPE.closed}
            label={fetchValueByKeyOfDropdown(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.programStatusPopupStatusList,
              STATUS_BADGE_TYPE.closed,
            )}
          />
        </MuiBox>

        <MuiBox data-testid="program-closure-information">
          {renderHeader(programSiteCoreKeys.closureInformationHeader)}
          <MuiGrid container className="form-container change-status-form">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(programSiteCoreKeys.primaryClosingReason)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={fetchValueByKeyOfDropdown(
                  programSiteCoreKeys.changeStatusModelPopupKey,
                  programSiteCoreKeys.primaryClosingReasonList,
                  getValueOrBlank(
                    programClosureData?.primaryClosingReason?.reasonSiteCoreKey,
                  ),
                )}
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(programSiteCoreKeys.secondaryClosingReason)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={fetchValueByKeyOfDropdown(
                  programSiteCoreKeys.changeStatusModelPopupKey,
                  programSiteCoreKeys.secondaryClosingReasonList,
                  getValueOrBlank(
                    programClosureData?.secondaryClosingReason
                      ?.reasonSiteCoreKey,
                  ),
                )}
              />
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container className="form-container closed-date">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(programSiteCoreKeys.closedDate)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={
                  programClosureData?.closedDate
                    ? convertToUserFormat(
                        programClosureData.closedDate,
                        userCountryDateFormat,
                      )
                    : ""
                }
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(programSiteCoreKeys.closedBy)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={getValueOrBlank(programClosureData?.closedBy)}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiBox>

        {programClosureData?.primaryClosingReason?.reasonSiteCoreKey ===
          PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_SITECORE_KEY && (
          <MuiBox
            data-testid="landed-information"
            className="landed-information"
          >
            {renderHeader(programSiteCoreKeys.landedInformationHeader)}
            <MuiTabs
              tabs={tabs}
              value={selectedTab}
              onChange={onTabChange}
              tabsTestIds={[tabIdGeneral, tabIdEmployer]}
            >
              <MuiTabPanel
                panelId={`${pageId}-panel1`}
                value={selectedTab}
                index={0}
                isPanelWithMount
                ariaLabelledById={tabIdGeneral}
                className="general-tab-panel"
              >
                <GeneralInformation
                  pageId={pageId}
                  userCountryDateFormat={userCountryDateFormat}
                  generalInformation={programClosureData?.generalInformation}
                  employerInformation={programClosureData?.employerInformation}
                  countryList={setApiDataToDropDownOption(
                    countryListData,
                    true,
                  )}
                  deliveryCountry={deliveryCountry}
                />
              </MuiTabPanel>
              <MuiTabPanel
                panelId={`${pageId}-panel2`}
                value={selectedTab}
                index={1}
                isPanelWithMount
                ariaLabelledById={tabIdEmployer}
                className="employee-tab-panel"
              >
                <EmployeeInformation
                  pageId={pageId}
                  userCountryDateFormat={userCountryDateFormat}
                  employerInformation={programClosureData?.employerInformation}
                  deliveryCountry={deliveryCountry}
                />
              </MuiTabPanel>
            </MuiTabs>
          </MuiBox>
        )}
      </ProgramClosureDetailsWrapper>
    </MuiModal>
  );
};
